/**
 * Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: LicenseRef-.amazon.com.-AmznSL-1.0
 * Licensed under the Amazon Software License  http://aws.amazon.com/asl/
 */

import _ from 'lodash';
import React from 'react';
import { observer, inject } from 'mobx-react';
import { makeObservable } from 'mobx';
import TimeAgo from 'react-timeago';
import { Header, Segment, Icon, Table } from 'semantic-ui-react';

import { swallowError } from '../../../helpers/utils';
import { withRouter } from '../../../helpers/routing';
import { isStoreError, isStoreReady, isStoreLoading, isStoreEmpty } from '../../../models/BaseStore';
import ErrorBox from '../../helpers/ErrorBox';
import ProgressPlaceHolder from '../../workflow-common/ProgressPlaceholder';

// expected props
// - workflowVersion (via props)
// - workflowsStore (via injection)
// - userDisplayName (via injection)
// - location (from react router)
class WorkflowAssignmentsList extends React.Component {
  constructor(props) {
    super(props);
    makeObservable(this, {});
  }

  componentDidMount() {
    const store = this.getAssignmentsStore();
    swallowError(store.load());
    store.startHeartbeat();
  }

  componentWillUnmount() {
    const store = this.getAssignmentsStore();
    store.stopHeartbeat();
  }

  getWorkflowVersion() {
    return this.props.workflowVersion;
  }

  getWorkflowStore() {
    const version = this.getWorkflowVersion();
    return this.props.workflowsStore.getWorkflowStore(version.id);
  }

  getAssignmentsStore() {
    const workflowStore = this.getWorkflowStore();
    return workflowStore.getAssignmentsStore();
  }

  getUserDisplayNameService() {
    return this.props.userDisplayName;
  }

  render() {
    const store = this.getAssignmentsStore();
    let content = null;

    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} className="p0" />;
    } else if (isStoreLoading(store)) {
      content = <ProgressPlaceHolder />;
    } else if (isStoreReady(store) && isStoreEmpty(store)) {
      content = this.renderEmptyAssignments();
    } else if (isStoreReady(store) && !isStoreEmpty(store)) {
      content = this.renderMain();
    } else {
      // We get here if the store is in the initial state
      content = null;
    }

    return <>{content}</>;
  }

  renderMain() {
    const store = this.getAssignmentsStore();
    const assignments = store.list;

    return <Segment padded>{this.renderAssignmentsTable(assignments)}</Segment>;
  }

  renderAssignmentsTable(assignments) {
    return (
      <Table basic="very" className="animated fadeIn">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={1}>Id</Table.HeaderCell>
            <Table.HeaderCell width={4}>Trigger</Table.HeaderCell>
            <Table.HeaderCell width={8}>Configuration</Table.HeaderCell>
            <Table.HeaderCell width={3}>Updated</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{_.map(assignments, assignment => this.renderAssignmentRow(assignment))}</Table.Body>
      </Table>
    );
  }

  renderAssignmentRow(assignment) {
    const { id, updatedAt, updatedBy, triggerType, triggerTypeData: config } = assignment;
    const displayNameService = this.getUserDisplayNameService();
    const isSystem = displayNameService.isSystem(updatedBy);
    const by = () => (isSystem ? '' : <span>by {displayNameService.getDisplayName(updatedBy)}</span>);

    return (
      <Table.Row key={id}>
        <Table.Cell>{id}</Table.Cell>
        <Table.Cell>{triggerType}</Table.Cell>
        <Table.Cell>{config}</Table.Cell>
        <Table.Cell>
          <TimeAgo date={updatedAt} />
          <div>{by()}&nbsp;</div>
        </Table.Cell>
      </Table.Row>
    );
  }

  renderEmptyAssignments() {
    return (
      <Segment placeholder>
        <Header icon className="color-grey">
          <Icon name="chain" />
          No assignments
          <Header.Subheader>
            Assignments allow you to configure the workflow to be triggered based on different criteria, try it out!
          </Header.Subheader>
        </Header>
      </Segment>
    );
  }
}

export default inject('workflowsStore', 'userDisplayName')(withRouter(observer(WorkflowAssignmentsList)));
